import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Tag, message, Tooltip, Popconfirm, Popover, Descriptions, Typography, Checkbox, Input, Modal, Progress, Badge } from 'antd';
// DAS-1701: Added Popover, Descriptions for rich rebalancing tooltip
// DAS-1726: Added Input, Modal for comment editing
// DAS-1779: Added SelectNowDrawer for "Select Now" functionality
// DAS-1867: Added Progress, Badge for sync to PROD feature
import { EyeOutlined, ReloadOutlined, LoadingOutlined, DeleteOutlined, EditOutlined, ThunderboltOutlined, CloudUploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import SelectNowDrawer from './SelectNowDrawer';

/**
 * DAS-1666: Run History Table
 * Displays list of completed backtest runs with ability to view historical results
 * DAS-1685: Added loading progress info for View button
 * DAS-1699: Added Conf ID column with click-to-view functionality
 */
const RunHistoryTable = ({ callAction, onViewResults, onViewConfiguration }) => {
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  
  // DAS-1685: Track which run is loading and show elapsed time
  const [loadingRunId, setLoadingRunId] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef(null);
  
  // DAS-1867: Sync to PROD state
  const [syncStatuses, setSyncStatuses] = useState({}); // {run_id: sync_log_entry}
  const [syncModal, setSyncModal] = useState({ visible: false, runId: null, progress: 0, message: '', status: 'idle' });
  
  // Detect if we're on DEV (localhost or dev subdomain)
  const isDev = window.location.hostname === 'localhost' 
             || window.location.hostname.includes('127.0.0.1')
             || window.location.hostname.includes('dev');

  useEffect(() => {
    fetchRunHistory();
  }, []);

  const fetchRunHistory = async () => {
    setLoading(true);
    try {
      const response = await callAction('list_recent_runs', {}, { useLoader: false });
      if (response?.success && response?.data) {
        setRuns(response.data);
        
        // DAS-1867: Fetch sync statuses for all runs (only on DEV)
        if (isDev && response.data.length > 0) {
          const runIds = response.data.map(r => r.run_id);
          try {
            const syncResponse = await callAction('get_all_sync_statuses', { run_ids: runIds }, { useLoader: false });
            if (syncResponse?.success && syncResponse?.data) {
              setSyncStatuses(syncResponse.data);
            }
          } catch (e) {
            console.warn('Could not fetch sync statuses:', e);
          }
        }
      } else {
        message.error('Failed to fetch run history');
      }
    } catch (error) {
      message.error(`Error fetching runs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // DAS-1685: Start elapsed time timer
  const startLoadingTimer = (runId) => {
    setLoadingRunId(runId);
    setElapsedSeconds(0);
    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
  };

  // DAS-1685: Stop elapsed time timer
  const stopLoadingTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setLoadingRunId(null);
    setElapsedSeconds(0);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const handleViewResults = async (run) => {
    // DAS-1685: Don't use table-level loading - only use button-level loading
    startLoadingTimer(run.run_id);
    try {
      console.log('[RunHistory] Loading results for run:', run.run_id, typeof run.run_id);
      const response = await callAction('get_run_results', { run_id: run.run_id }, { useLoader: false });
      if (response?.success && response?.data) {
        onViewResults(run, response.data);
      } else {
        message.error('Failed to load run results');
      }
    } catch (error) {
      message.error(`Error loading results: ${error.message}`);
    } finally {
      stopLoadingTimer();
    }
  };

  // DAS-1867: Sync run to PROD handler with progress simulation
  const handleSyncToProd = async (run) => {
    setSyncModal({ 
      visible: true, 
      runId: run.run_id, 
      progress: 0, 
      message: 'Connecting to PROD...', 
      status: 'syncing',
      startTime: Date.now()
    });
    
    // Progress simulation stages (approximate % and message)
    const stages = [
      { pct: 5, msg: 'Fetching run data from DEV...' },
      { pct: 10, msg: 'Connecting to PROD via SSH...' },
      { pct: 15, msg: 'Copying portfolio & configuration...' },
      { pct: 20, msg: 'Copying run metadata...' },
      { pct: 35, msg: 'Copying events (batch insert)...' },
      { pct: 55, msg: 'Copying daily positions (batch insert)...' },
      { pct: 70, msg: 'Copying trade signals...' },
      { pct: 85, msg: 'Copying rebalancing selections...' },
      { pct: 95, msg: 'Finalizing sync...' },
    ];
    
    let stageIdx = 0;
    const progressInterval = setInterval(() => {
      setSyncModal(prev => {
        if (prev.status !== 'syncing') {
          clearInterval(progressInterval);
          return prev;
        }
        const elapsed = Math.round((Date.now() - prev.startTime) / 1000);
        
        if (stageIdx < stages.length) {
          const stage = stages[stageIdx];
          stageIdx++;
          return {
            ...prev,
            progress: stage.pct,
            message: `${stage.msg} (${elapsed}s)`
          };
        } else {
          // All stages done, just update elapsed time
          return {
            ...prev,
            message: `Finalizing... (${elapsed}s)`
          };
        }
      });
    }, 2000); // Update every 2 seconds
    
    try {
      const response = await callAction('sync_run_to_prod', { run_id: run.run_id }, { useLoader: false });
      clearInterval(progressInterval);
      
      if (response?.success && response?.data?.success) {
        const result = response.data;
        const duration = result.duration_ms ? (result.duration_ms / 1000).toFixed(1) : 
                         Math.round((Date.now() - syncModal.startTime) / 1000);
        
        setSyncModal(prev => ({ 
          ...prev, 
          progress: 100, 
          message: `✅ Synced successfully!`,
          status: 'success',
          result: { ...result, duration_display: duration }
        }));
        
        // Update sync statuses
        setSyncStatuses(prev => ({
          ...prev,
          [run.run_id]: {
            source_run_id: run.run_id,
            target_run_id: result.target_run_id,
            status: 'completed',
            rows_copied: result.rows_copied
          }
        }));
        
        message.success(`Run #${run.run_id} synced to PROD as #${result.target_run_id}`);
      } else {
        const errorMsg = response?.data?.error || response?.error || 'Unknown error';
        setSyncModal(prev => ({ 
          ...prev, 
          progress: 0, 
          message: `❌ Failed: ${errorMsg}`,
          status: 'error'
        }));
        message.error(`Sync failed: ${errorMsg}`);
      }
    } catch (error) {
      clearInterval(progressInterval);
      setSyncModal(prev => ({ 
        ...prev, 
        progress: 0, 
        message: `❌ Error: ${error.message}`,
        status: 'error'
      }));
      message.error(`Sync error: ${error.message}`);
    }
  };

  // DAS-1692: Delete run handler
  const handleDeleteRun = async (run) => {
    try {
      const response = await callAction('delete_run', { run_id: run.run_id }, { useLoader: false });
      if (response?.success) {
        message.success(`Run #${run.run_id} deleted successfully`);
        fetchRunHistory(); // Refresh the table
      } else {
        message.error(response?.error || 'Failed to delete run');
      }
    } catch (error) {
      message.error(`Error deleting run: ${error.message}`);
    }
  };

  // DAS-2211: Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkDeleting(true);
    try {
      const response = await callAction('delete_runs_bulk', { run_ids: selectedRowKeys }, { useLoader: false });
      if (response?.success) {
        const data = response.data || {};
        const deletedCount = data.deleted_count || selectedRowKeys.length;
        message.success(`Deleted ${deletedCount} run${deletedCount !== 1 ? 's' : ''} successfully`);
        setSelectedRowKeys([]);
        fetchRunHistory();
      } else {
        message.error(response?.error || 'Failed to delete runs');
      }
    } catch (error) {
      message.error(`Error deleting runs: ${error.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  // DAS-1711: Update keep/retest flags
  const handleFlagChange = async (runId, flagName, checked) => {
    try {
      const payload = { run_id: runId, [flagName]: checked };
      const response = await callAction('update_run_flags', payload, { useLoader: false });
      if (response?.success) {
        // Update local state immediately for responsive UI
        setRuns(prevRuns => prevRuns.map(run => 
          run.run_id === runId ? { ...run, [flagName]: checked ? 1 : 0 } : run
        ));
      } else {
        message.error(response?.error || `Failed to update ${flagName}`);
      }
    } catch (error) {
      message.error(`Error updating flag: ${error.message}`);
    }
  };

  // DAS-2211: Multi-select state for bulk deletion
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // DAS-1726: Comment editing state
  const [editingComment, setEditingComment] = useState({ visible: false, runId: null, comment: '' });

  // DAS-1779: Select Now drawer state
  const [selectNowDrawer, setSelectNowDrawer] = useState({ open: false, runId: null });

  // DAS-1726: Update run comment
  const handleCommentSave = async () => {
    try {
      const payload = { run_id: editingComment.runId, user_comment: editingComment.comment };
      const response = await callAction('update_run_comment', payload, { useLoader: false });
      if (response?.success) {
        // Update local state immediately for responsive UI
        setRuns(prevRuns => prevRuns.map(run => 
          run.run_id === editingComment.runId ? { ...run, user_comment: editingComment.comment } : run
        ));
        setEditingComment({ visible: false, runId: null, comment: '' });
        message.success('Comment updated');
      } else {
        message.error(response?.error || 'Failed to update comment');
      }
    } catch (error) {
      message.error(`Error updating comment: ${error.message}`);
    }
  };

  const columns = [
    {
      title: 'Run ID',
      dataIndex: 'run_id',
      key: 'run_id',
      width: 80,
      render: (id) => <strong>#{id}</strong>
    },
    {
      // DAS-1711: Keep flag - marks runs to keep (trusted results)
      title: <Tooltip title="Keep: Mark runs with trusted results to preserve">Keep</Tooltip>,
      dataIndex: 'flag_keep',
      key: 'flag_keep',
      width: 60,
      align: 'center',
      render: (val, record) => (
        <Checkbox
          checked={!!val}
          onChange={(e) => handleFlagChange(record.run_id, 'flag_keep', e.target.checked)}
        />
      )
    },
    {
      // DAS-1711: Retest flag - marks runs that need re-running
      title: <Tooltip title="Retest: Mark runs that need to be re-run">Retest</Tooltip>,
      dataIndex: 'flag_retest',
      key: 'flag_retest',
      width: 60,
      align: 'center',
      render: (val, record) => (
        <Checkbox
          checked={!!val}
          onChange={(e) => handleFlagChange(record.run_id, 'flag_retest', e.target.checked)}
        />
      )
    },
    {
      // DAS-1699: Add Conf ID column with click-to-view
      title: 'Conf ID',
      dataIndex: 'configuration_id',
      key: 'configuration_id',
      width: 80,
      render: (configId) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => onViewConfiguration && onViewConfiguration(configId)}
          disabled={!onViewConfiguration}
        >
          #{configId}
        </Button>
      )
    },
    {
      // DAS-1726: Comment column - editable user note
      title: 'Comment',
      dataIndex: 'user_comment',
      key: 'user_comment',
      width: 100,
      render: (comment, record) => {
        const displayText = comment ? (comment.length > 10 ? comment.substring(0, 10) + '...' : comment) : '';
        return (
          <Tooltip title={comment || 'Click to add comment'}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ fontSize: 10, opacity: comment ? 0.5 : 1 }} />}
              style={{ 
                padding: '0 4px', 
                height: 'auto', 
                fontSize: 11, 
                fontFamily: 'monospace',
                maxWidth: 90,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              onClick={() => setEditingComment({ 
                visible: true, 
                runId: record.run_id, 
                comment: comment || '' 
              })}
            >
              {displayText || '-'}
            </Button>
          </Tooltip>
        );
      }
    },
    {
      // DAS-1710: Min Market Cap column
      title: <Tooltip title="Minimum market cap filter for universe selection">Min Cap</Tooltip>,
      dataIndex: 'min_market_cap_eur',
      key: 'min_market_cap_eur',
      width: 80,
      align: 'right',
      render: (val) => {
        if (!val) return <span style={{ color: '#999' }}>-</span>;
        const billions = val / 1000000000;
        return <span style={{ fontFamily: 'monospace' }}>€{billions.toFixed(0)}B</span>;
      }
    },
    {
      // DAS-1701: Rebalancing scenario label with rich tooltip
      title: 'Rebalancing',
      dataIndex: 'rebalancing_label',
      key: 'rebalancing_label',
      width: 140,
      render: (label, record) => {
        const cfg = record.rebalancing_config;
        if (!cfg || !cfg.enabled) {
          return <Tag color="default">-</Tag>;
        }
        
        const factor = cfg.factor_strategy || {};
        const presetLabel = factor.preset === 'value' ? 'Value' : factor.preset === 'growth' ? 'Growth' : 'Custom';
        
        const popoverContent = (
          <div style={{ width: 320 }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Frequency">
                {cfg.frequency_days} days {cfg.frequency_days === 63 ? '(quarterly)' : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Selection percentile">
                {cfg.selection_percentile}% (top {(100 - cfg.selection_percentile).toFixed(1)}%)
              </Descriptions.Item>
              <Descriptions.Item label="Sector-neutral">
                <Tag color={cfg.sector_neutral ? 'green' : 'orange'}>
                  {cfg.sector_neutral ? 'Yes' : 'No (Cross-Sectional)'}
                </Tag>
              </Descriptions.Item>
              {!cfg.sector_neutral && (
                <Descriptions.Item label="Max Sector Exposure">
                  {cfg.max_sector_exposure_pct}%
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Z-Score Baseline">
                <Tag color={cfg.z_score_baseline === 'market' ? 'blue' : 'default'}>
                  {cfg.z_score_baseline === 'market' ? 'Market (sector benchmarks)' : 'Universe (on-the-fly)'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Factor Strategy">
                <div>
                  <Tag color="purple">{presetLabel}</Tag>
                  {factor.preset === 'custom' && (
                    <span style={{ marginLeft: 8, fontSize: 11 }}>
                      ROA: {factor.roa_weight}%, P/B: {factor.pb_weight}%
                    </span>
                  )}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Formula">
                <Typography.Text code style={{ fontSize: 11 }}>
                  {factor.preset === 'growth' || factor.pb_weight === 0
                    ? 'score = ROA_zscore'
                    : factor.preset === 'value'
                    ? 'score = 0.50 × ROA_z − 0.50 × PB_z'
                    : `score = ${(factor.roa_weight/100).toFixed(2)} × ROA_z − ${(factor.pb_weight/100).toFixed(2)} × PB_z`
                  }
                </Typography.Text>
              </Descriptions.Item>
            </Descriptions>
          </div>
        );
        
        return (
          <Popover content={popoverContent} title="Rebalancing Configuration" trigger="hover" placement="right">
            <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}>
              {label || '-'}
            </Tag>
          </Popover>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const color = status === 'completed' ? 'green' : status === 'failed' ? 'red' : 'orange';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Portfolio',
      dataIndex: 'portfolio_name',
      key: 'portfolio_name',
      ellipsis: true,
      render: (name) => name || '-'
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => {
        if (!record.start_date || !record.end_date) return '-';
        const start = new Date(record.start_date).toLocaleDateString();
        const end = new Date(record.end_date).toLocaleDateString();
        return (
          <Tooltip title={`${start} to ${end}`}>
            <span>{start} → {end}</span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 160,
      render: (date) => date ? new Date(date).toLocaleString() : '-'
    },
    {
      title: 'Finished',
      dataIndex: 'finished_at',
      key: 'finished_at',
      width: 160,
      render: (date) => date ? new Date(date).toLocaleString() : '-'
    },
    {
      title: 'Progress',
      dataIndex: 'progress_pct',
      key: 'progress_pct',
      width: 80,
      render: (pct) => pct ? `${Number(pct).toFixed(0)}%` : '-'
    },
    {
      title: 'Result% (TWR)',
      dataIndex: 'strategy_twr_pct',
      key: 'strategy_twr_pct',
      width: 110,
      render: (val) => {
        if (val == null) return <span style={{ color: '#999' }}>-</span>;
        const num = Number(val);
        const color = num >= 0 ? '#52c41a' : '#cf1322';
        const sign = num >= 0 ? '+' : '';
        return <span style={{ color, fontWeight: 'bold' }}>{sign}{num.toFixed(2)}%</span>;
      }
    },
    {
      // DAS-2344: Max drawdown — peak-to-trough on strategy equity. Always
      // negative or zero. NULL for runs that pre-date this column (no backfill).
      title: <Tooltip title="Maximum peak-to-trough drawdown on strategy equity (DAS-2344). Lower magnitude = smaller losses during crashes.">Max DD</Tooltip>,
      dataIndex: 'max_dd_pct',
      key: 'max_dd_pct',
      width: 90,
      sorter: (a, b) => (a.max_dd_pct ?? 0) - (b.max_dd_pct ?? 0),
      render: (val) => {
        if (val == null) return <span style={{ color: '#999' }}>-</span>;
        const num = Number(val);
        // Drawdown is always ≤0; display in red if non-zero.
        return <span style={{ color: num < 0 ? '#cf1322' : '#999', fontWeight: 'bold' }}>{num.toFixed(2)}%</span>;
      }
    },
    {
      title: 'Excess vs B&H',
      dataIndex: 'excess_twr_pct',
      key: 'excess_twr_pct',
      width: 110,
      render: (val) => {
        if (val == null) return <span style={{ color: '#999' }}>-</span>;
        const num = Number(val);
        const color = num >= 0 ? '#52c41a' : '#cf1322';
        const sign = num >= 0 ? '+' : '';
        return <span style={{ color, fontWeight: 'bold' }}>{sign}{num.toFixed(2)}%</span>;
      }
    },
    {
      // DAS-3214: return per unit of swing (Sharpe ratio), you vs the run's SECONDARY
      // benchmark; figures written by scripts/backtest_sharpe_backfill.py (DAS-3213).
      title: (
        <Tooltip title="Return above the ECB deposit rate divided by the yearly swing, over this run's own dates; the benchmark is the run's secondary benchmark over the same dates.">
          Per unit of swing
        </Tooltip>
      ),
      dataIndex: 'strategy_sharpe',
      key: 'strategy_sharpe',
      width: 150,
      render: (val, record) => {
        // blank until the run has a figure; "bench -" when the run has no benchmark figure
        if (val == null) {
          return <span data-testid="run-sharpe-cell" />;
        }
        const youNum = Number(val);
        const benchNum = record.benchmark_sharpe == null ? null : Number(record.benchmark_sharpe);
        const green = { color: '#52c41a', fontWeight: 'bold' };
        const youStyle = benchNum != null && youNum >= benchNum ? green : undefined;
        const benchStyle = benchNum != null && benchNum >= youNum ? green : undefined;
        return (
          <span data-testid="run-sharpe-cell">
            <span style={youStyle}>you {youNum.toFixed(2)}</span>
            {' / '}
            <span style={benchStyle}>bench {benchNum == null ? '-' : benchNum.toFixed(2)}</span>
          </span>
        );
      }
    },
    {
      title: 'Excess vs 2nd BM',
      dataIndex: 'secondary_excess_twr_pct',
      key: 'secondary_excess_twr_pct',
      width: 130,
      render: (val, record) => {
        if (val == null) return <span style={{ color: '#999' }}>-</span>;
        const num = Number(val);
        const color = num >= 0 ? '#52c41a' : '#cf1322';
        const sign = num >= 0 ? '+' : '';
        return (
          <div>
            <span style={{ color, fontWeight: 'bold' }}>{sign}{num.toFixed(2)}%</span>
            {record.secondary_benchmark && (
              <div style={{ fontSize: '10px', color: '#888' }}>{record.secondary_benchmark}</div>
            )}
          </div>
        );
      }
    },
    // DAS-1867: PROD Sync column (only shown on DEV)
    ...(isDev ? [{
      title: <Tooltip title="Sync status to PROD environment">PROD Sync</Tooltip>,
      key: 'prod_sync',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (record.status !== 'completed') {
          return <span style={{ color: '#999' }}>-</span>;
        }
        
        const syncInfo = syncStatuses[record.run_id];
        
        if (syncInfo && syncInfo.status === 'completed') {
          return (
            <Tooltip title={`Synced to PROD as Run #${syncInfo.target_run_id} (${syncInfo.rows_copied?.toLocaleString() || '?'} rows)`}>
              <Badge status="success" />
              <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginLeft: 4 }}>
                #{syncInfo.target_run_id}
              </Tag>
            </Tooltip>
          );
        }
        
        if (syncInfo && syncInfo.status === 'in_progress') {
          return <Tag color="processing" icon={<LoadingOutlined />}>Syncing...</Tag>;
        }
        
        if (syncInfo && syncInfo.status === 'failed') {
          return (
            <Tooltip title={syncInfo.error_message || 'Sync failed'}>
              <Tag color="error">Failed</Tag>
            </Tooltip>
          );
        }
        
        // Not synced - show sync button
        return (
          <Popconfirm
            title="Sync to PROD?"
            description={`Copy run #${record.run_id} and all its data to PROD with new IDs.`}
            onConfirm={() => handleSyncToProd(record)}
            okText="Sync"
            cancelText="Cancel"
          >
            <Button 
              size="small" 
              icon={<CloudUploadOutlined />}
              disabled={loadingRunId !== null || syncModal.visible}
            >
              Sync→
            </Button>
          </Popconfirm>
        );
      }
    }] : []),
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_, record) => {
        const isThisRunLoading = loadingRunId === record.run_id;
        // DAS-1685: Show meaningful progress based on elapsed time
        const getLoadingText = () => {
          if (elapsedSeconds < 2) return 'Fetching data...';
          if (elapsedSeconds < 5) return 'Processing equity curve...';
          if (elapsedSeconds < 10) return 'Calculating TWR...';
          if (elapsedSeconds < 15) return 'Building charts...';
          return `Still loading... (${elapsedSeconds}s)`;
        };
        return (
          <Space>
            {record.status === 'completed' && (
              <>
                <Button
                  type="primary"
                  size="small"
                  icon={isThisRunLoading ? <LoadingOutlined spin /> : <EyeOutlined />}
                  onClick={() => handleViewResults(record)}
                  disabled={loadingRunId !== null}
                  style={{ minWidth: isThisRunLoading ? 160 : 70 }}
                >
                  {isThisRunLoading ? getLoadingText() : 'View'}
                </Button>
                {/* DAS-1779: Select Now button */}
                <Tooltip title="Calculate what stocks to trade TODAY based on this run's config">
                  <Button
                    size="small"
                    icon={<ThunderboltOutlined />}
                    onClick={() => setSelectNowDrawer({ open: true, runId: record.run_id })}
                    disabled={loadingRunId !== null}
                  >
                    Select Now
                  </Button>
                </Tooltip>
              </>
            )}
            {/* DAS-1692: Delete button with confirmation */}
            <Popconfirm
              title="Delete this run?"
              description={`This will permanently delete run #${record.run_id} and all its data.`}
              onConfirm={() => handleDeleteRun(record)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={loadingRunId !== null}
              />
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div>
            <strong>Recent Backtest Runs</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>({runs.length} runs)</span>
          </div>
          {/* DAS-2211: Bulk delete button — visible when rows selected */}
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} run${selectedRowKeys.length !== 1 ? 's' : ''}?`}
              description="This will permanently delete all selected runs and their data."
              onConfirm={handleBulkDelete}
              okText="Delete All"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={bulkDeleting}
              >
                Delete {selectedRowKeys.length} selected
              </Button>
            </Popconfirm>
          )}
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchRunHistory}
          loading={loading}
        >
          Refresh
        </Button>
      </div>
      <Table
        rowKey="run_id"
        loading={loading}
        columns={columns}
        dataSource={runs}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} runs` }}
        size="small"
        scroll={{ x: 1600 }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
      />

      {/* DAS-1726: Comment editing modal */}
      <Modal
        title={`Edit Comment for Run #${editingComment.runId}`}
        open={editingComment.visible}
        onOk={handleCommentSave}
        onCancel={() => setEditingComment({ visible: false, runId: null, comment: '' })}
        okText="Save"
        cancelText="Cancel"
      >
        <Input.TextArea
          value={editingComment.comment}
          onChange={(e) => setEditingComment({ ...editingComment, comment: e.target.value })}
          placeholder="Add a short description for this run (max 255 characters)"
          maxLength={255}
          showCount
          rows={3}
          autoFocus
        />
      </Modal>

      {/* DAS-1779: Select Now drawer */}
      <SelectNowDrawer
        open={selectNowDrawer.open}
        onClose={() => setSelectNowDrawer({ open: false, runId: null })}
        runId={selectNowDrawer.runId}
        callAction={callAction}
      />
      
      {/* DAS-1867: Sync to PROD progress modal */}
      <Modal
        title={<><CloudUploadOutlined /> Syncing Run #{syncModal.runId} to PROD</>}
        open={syncModal.visible}
        footer={
          syncModal.status === 'syncing' ? null : (
            <Button type="primary" onClick={() => setSyncModal({ visible: false, runId: null, progress: 0, message: '', status: 'idle' })}>
              Close
            </Button>
          )
        }
        closable={syncModal.status !== 'syncing'}
        onCancel={() => {
          if (syncModal.status !== 'syncing') {
            setSyncModal({ visible: false, runId: null, progress: 0, message: '', status: 'idle' });
          }
        }}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {syncModal.status === 'syncing' && (
            <>
              <Progress 
                type="circle" 
                percent={syncModal.progress} 
                status="active"
                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
              />
              <p style={{ marginTop: 16, color: '#666' }}>{syncModal.message}</p>
            </>
          )}
          
          {syncModal.status === 'success' && (
            <>
              <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
              <p style={{ marginTop: 16, fontSize: 16 }}>{syncModal.message}</p>
              {syncModal.result && (
                <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 8, textAlign: 'left' }}>
                  <p><strong>Source (DEV):</strong> Run #{syncModal.result.source_run_id}</p>
                  <p><strong>Target (PROD):</strong> Run #{syncModal.result.target_run_id}</p>
                  <p><strong>Rows copied:</strong> {syncModal.result.rows_copied?.toLocaleString()}</p>
                  <p><strong>Duration:</strong> {(syncModal.result.duration_ms / 1000).toFixed(1)}s</p>
                </div>
              )}
            </>
          )}
          
          {syncModal.status === 'error' && (
            <>
              <DeleteOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
              <p style={{ marginTop: 16, color: '#ff4d4f' }}>{syncModal.message}</p>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RunHistoryTable;

